import React, { useEffect, useState, useCallback } from "react";
import { useScrollLock } from "../hooks/useScrollLock";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getAdminIncidents, getEmployees, deleteAdminIncident } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { cloudinaryThumb, cloudinaryPreview } from "../services/cloudinary";
import { ChevronLeft, ChevronRight, AlertTriangle, X, Trash2, MapPin, MessageSquare, FileText, Eye } from "lucide-react";

const LAST_SEEN_KEY = "incidents_last_seen";
const LIMIT = 50;

const INCIDENT_LABELS = {
  "1A": "CAMBIO UBICACIÓN MEDIDOR - Accesible",
  "1V": "CAMBIO UBICACIÓN MEDIDOR - Vivienda",
  "31": "OTRO - Olor a gas",
  "34": "OTRO - Texto Libre",
  "AA": "AUSENTE",
  "B":  "Datos del medidor erróneo",
  "D":  "No se ubica el suministro",
  "FC": "CRISTAL ROTO - Con Lectura",
  "FS": "CRISTAL ROTO - Sin Lectura",
  "GC": "VIDRIO EMPAÑADO/OPACO - Con Lectura",
  "GS": "VIDRIO EMPAÑADO/OPACO - Sin Lectura",
  "H":  "Cambio de Medidor",
  "K":  "Comprobar el funcionamiento del medidor",
  "L":  "Medidor retirado",
  "N":  "Variación de Datos del Suministro",
  "R1": "PUERTA TRABADA - Marco Hinchado",
  "R2": "PUERTA TRABADA - Puerta Antireglamentaria",
  "R3": "PUERTA TRABADA - Cerradura Rota",
  "R4": "PUERTA TRABADA - Otra (Texto Libre)",
  "S1": "ILICITO - Conexión clandestina total - By Pass",
  "S2": "ILICITO - Conexión clandestina parcial (paralela al medidor)",
  "S3": "ILICITO - Medidor invertido",
  "S4": "ILICITO - Adulteración de precintos de seguridad del medidor",
  "ZA": "ACCESO BLOQUEADO - Auto",
  "ZB": "ACCESO BLOQUEADO - Medidor Tapiado",
  "ZC": "ACCESO BLOQUEADO - Cerrado con Llave / Candado",
  "ZF": "ACCESO BLOQUEADO - Fuera de Radio / Cambio de Turno",
  "ZG": "ACCESO BLOQUEADO - No permiten ingreso",
  "ZL": "ACCESO BLOQUEADO - Lluvias / Inundado",
  "ZM": "ACCESO BLOQUEADO - Peligro Momentáneo",
  "ZP": "ACCESO BLOQUEADO - Perro",
  "ZR": "ACCESO BLOQUEADO - Tapado por basura / Ramas",
  "ZT": "ACCESO BLOQUEADO - Otros (texto libre)",
};

function fmt(dt) {
  try { return format(new Date(dt), "dd/MM/yyyy HH:mm", { locale: es }); }
  catch { return dt; }
}

function PhotoCard({ url, label, onLightbox }) {
  if (!url) return null;
  return (
    <div style={s.photoCard}>
      <div style={s.photoCardLabel}>{label}</div>
      <img
        src={cloudinaryThumb(url, 180)}
        alt={label}
        style={s.photoCardImg}
        onClick={() => onLightbox(cloudinaryPreview(url))}
      />
    </div>
  );
}

function DetailModal({ inc, onClose, onDelete, isAdminPlus }) {
  const [lightbox, setLightbox] = useState(null);
  const label = INCIDENT_LABELS[inc.code];
  const photos = [
    { url: inc.image_url,         label: "Medidor / Nicho" },
    { url: inc.photo_fachada_url, label: "Fachada" },
    { url: inc.photo_aviso_url,   label: "Aviso de Visita" },
  ].filter((p) => p.url);

  return (
    <>
      <div style={s.modalOverlay} onClick={onClose}>
        <div style={s.modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={s.modalHeader}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0 }}>
              <div style={s.modalCodeBadge}>{inc.code}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.modalTitle}>{label || inc.code}</div>
                <div style={s.modalMeta}>
                  <span style={{ fontWeight: 700 }}>{inc.user_name || "—"}</span>
                  {inc.user_email && <span style={{ color: "var(--text-3)" }}> · {inc.user_email}</span>}
                  <span style={{ color: "var(--text-3)" }}> · {fmt(inc.created_at)}</span>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {isAdminPlus && (
                <button style={s.deleteBtn} onClick={() => { onClose(); onDelete(inc); }} title="Eliminar">
                  <Trash2 size={15} />
                </button>
              )}
              <button style={s.closeBtn} onClick={onClose}><X size={18} /></button>
            </div>
          </div>

          <div style={s.modalBody}>
            {/* Fotos */}
            {photos.length > 0 && (
              <div style={s.section}>
                <div style={s.sectionTitle}>Fotos</div>
                <div style={s.photosGrid}>
                  {photos.map((p) => (
                    <PhotoCard key={p.label} url={p.url} label={p.label} onLightbox={setLightbox} />
                  ))}
                </div>
              </div>
            )}

            {/* Datos */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Datos</div>
              <div style={s.dataGrid}>
                {inc.meter_code && (
                  <div style={s.dataItem}>
                    <div style={s.dataLabel}>Medidor</div>
                    <div style={s.dataValue}>{inc.meter_code}</div>
                  </div>
                )}
                {inc.description && (
                  <div style={{ ...s.dataItem, gridColumn: "1 / -1" }}>
                    <div style={s.dataLabel}><FileText size={12} style={{ marginRight: 4 }} />Descripción</div>
                    <div style={s.dataValue}>{inc.description}</div>
                  </div>
                )}
                {inc.comment && (
                  <div style={{ ...s.dataItem, gridColumn: "1 / -1" }}>
                    <div style={s.dataLabel}><MessageSquare size={12} style={{ marginRight: 4 }} />Comentario</div>
                    <div style={s.dataValue}>{inc.comment}</div>
                  </div>
                )}
                {inc.latitude && inc.longitude && (
                  <div style={s.dataItem}>
                    <div style={s.dataLabel}><MapPin size={12} style={{ marginRight: 4 }} />Ubicación</div>
                    <a
                      href={`https://www.google.com/maps?q=${inc.latitude},${inc.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      style={s.mapLink}
                    >
                      Ver en Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
          <button style={s.lightboxClose} onClick={() => setLightbox(null)}><X size={20} color="#fff" /></button>
          <img src={lightbox} alt="foto" style={s.lightboxImg} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}

export default function IncidentsPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const [incidents, setIncidents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  useScrollLock(!!detail || !!deleteTarget);
  const [filters, setFilters] = useState({ userId: "", from: "", to: "", code: "", meterCode: "" });

  useEffect(() => {
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    window.dispatchEvent(new Event("incidents_seen"));
  }, []);

  useEffect(() => {
    getEmployees().then(setEmployees).catch(() => {});
  }, []);

  const load = useCallback(
    (p = 1) => {
      setLoading(true);
      getAdminIncidents({ page: p, limit: LIMIT, ...filters })
        .then((d) => {
          setIncidents(d.data);
          setTotal(d.pagination.total);
          setPages(d.pagination.pages);
          setPage(p);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    },
    [filters],
  );

  useEffect(() => { load(1); }, [load]);

  function applyFilters(e) { e.preventDefault(); load(1); }
  function clearFilters() { setFilters({ userId: "", from: "", to: "", code: "", meterCode: "" }); }
  function handleDelete(inc) { setDeleteTarget(inc); }

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div style={s.page}>
      <DeleteConfirmModal
        visible={!!deleteTarget}
        message={`¿Eliminar la incidencia ${deleteTarget?.code} de ${deleteTarget?.user_name || "este usuario"}?`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await deleteAdminIncident(deleteTarget.id);
          setIncidents((prev) => prev.filter((i) => i.id !== deleteTarget.id));
          setTotal((t) => t - 1);
          setDeleteTarget(null);
        }}
      />

      {detail && (
        <DetailModal
          inc={detail}
          onClose={() => setDetail(null)}
          onDelete={handleDelete}
          isAdminPlus={isAdminPlus}
        />
      )}

      {/* Header */}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AlertTriangle size={20} color="#d97706" />
          <h1 style={s.title}>Incidencias</h1>
        </div>
        <span style={s.countBadge}>{total} total</span>
      </div>

      {/* Filters */}
      <form style={s.filterBar} onSubmit={applyFilters}>
        <select
          style={s.select}
          value={filters.userId}
          onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
        >
          <option value="">Todos los usuarios</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
        <input style={s.input} type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
        <input style={s.input} type="date" value={filters.to}   onChange={(e) => setFilters((f) => ({ ...f, to:   e.target.value }))} />
        <input style={{ ...s.input, width: 110 }} placeholder="Código" value={filters.code} onChange={(e) => setFilters((f) => ({ ...f, code: e.target.value }))} />
        <input style={{ ...s.input, width: 130 }} placeholder="Nº medidor" value={filters.meterCode} onChange={(e) => setFilters((f) => ({ ...f, meterCode: e.target.value }))} />
        <button style={s.btnPrimary} type="submit">Buscar</button>
        {hasFilters && (
          <button style={s.btnGhost} type="button" onClick={clearFilters}>
            <X size={14} style={{ marginRight: 4 }} />Limpiar
          </button>
        )}
      </form>

      {/* Table */}
      <div style={s.card}>
        {loading ? (
          <div style={s.empty}>Cargando…</div>
        ) : incidents.length === 0 ? (
          <div style={s.empty}>No hay incidencias para los filtros seleccionados.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {["Fecha", "Usuario", "Código", "Medidor", "Fotos", ""].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => {
                const photos = [inc.image_url, inc.photo_fachada_url, inc.photo_aviso_url].filter(Boolean);
                const label = INCIDENT_LABELS[inc.code];
                return (
                  <tr
                    key={inc.id}
                    style={{ ...s.tr, cursor: "pointer" }}
                    onClick={() => setDetail(inc)}
                  >
                    <td style={s.td}>{fmt(inc.created_at)}</td>
                    <td style={s.td}>
                      <span style={s.userName}>{inc.user_name || "—"}</span>
                      {inc.user_email && <span style={s.userEmail}>{inc.user_email}</span>}
                    </td>
                    <td style={s.td}>
                      <span style={s.codeBadge} title={label}>{inc.code}</span>
                      {label && <span style={s.codeLabel}>{label}</span>}
                    </td>
                    <td style={s.td}>
                      {inc.meter_code || <span style={{ color: "var(--text-3)" }}>—</span>}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: "flex", gap: 4 }}>
                        {photos.length === 0 && <span style={{ color: "var(--text-3)" }}>—</span>}
                        {photos.map((url, i) => (
                          <img
                            key={i}
                            src={cloudinaryThumb(url, 48)}
                            alt="foto"
                            style={s.thumb}
                            onClick={(e) => { e.stopPropagation(); setDetail(inc); }}
                          />
                        ))}
                      </div>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button
                          style={s.viewBtn}
                          title="Ver detalle"
                          onClick={(e) => { e.stopPropagation(); setDetail(inc); }}
                        >
                          <Eye size={13} />
                          <span>Ver</span>
                        </button>
                        {isAdminPlus && (
                          <button
                            style={s.deleteBtn}
                            title="Eliminar"
                            onClick={(e) => { e.stopPropagation(); handleDelete(inc); }}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={s.pagination}>
          <button style={s.pageBtn} disabled={page <= 1} onClick={() => load(page - 1)}>
            <ChevronLeft size={16} />
          </button>
          <span style={s.pageInfo}>Página {page} de {pages}</span>
          <button style={s.pageBtn} disabled={page >= pages} onClick={() => load(page + 1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  page:   { width: "100%" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 },
  title:  { fontSize: 22, fontWeight: 700, color: "var(--text)", margin: 0 },
  countBadge: { background: "var(--warning-bg)", color: "var(--warning-text)", borderRadius: 100, padding: "4px 12px", fontSize: 13, fontWeight: 700 },

  filterBar: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 20, borderRadius: 12, padding: "14px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  select:     { padding: "8px 12px", borderRadius: 10, fontSize: 13, background: "var(--bg-subtle)", color: "var(--text)", fontFamily: "Montserrat, sans-serif", minWidth: 200 },
  input:      { padding: "8px 12px", borderRadius: 10, fontSize: 13, background: "var(--bg-subtle)", color: "var(--text)", fontFamily: "Montserrat, sans-serif" },
  btnPrimary: { padding: "8px 18px", borderRadius: 10, border: "none", background: "#D4620D", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  btnGhost:   { padding: "8px 14px", borderRadius: 10, color: "var(--text-2)", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", fontFamily: "Montserrat, sans-serif" },

  card:   { borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "auto" },
  empty:  { padding: 48, textAlign: "center", color: "var(--text-2)", fontSize: 14 },
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:     { padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "var(--text-2)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" },
  tr:     { borderBottom: "1px solid var(--border)", transition: "background 0.1s" },
  td:     { padding: "11px 16px", color: "var(--text)", verticalAlign: "middle" },
  userName:  { display: "block", fontWeight: 700, color: "var(--text)" },
  userEmail: { display: "block", fontSize: 11, color: "var(--text-2)", marginTop: 1 },
  codeBadge: { display: "inline-block", background: "var(--warning-bg)", color: "var(--warning-text)", borderRadius: 6, padding: "2px 8px", fontWeight: 700, fontSize: 12, letterSpacing: "0.03em" },
  codeLabel: { display: "block", fontSize: 11, color: "var(--text-2)", marginTop: 3, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  thumb:     { width: 40, height: 40, objectFit: "cover", borderRadius: 8, cursor: "pointer", transition: "transform 0.15s" },
  viewBtn:   { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-2)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  deleteBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: "none", background: "var(--error-bg)", color: "var(--error-text)", cursor: "pointer" },

  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 24 },
  pageBtn:    { width: 36, height: 36, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text)" },
  pageInfo:   { fontSize: 13, color: "var(--text-2)", fontWeight: 700 },

  // Detail modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: 20 },
  modal:        { background: "var(--bg-card)", borderRadius: 20, width: "100%", maxWidth: 680, maxHeight: "88vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.22)" },
  modalHeader:  { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "20px 20px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  modalCodeBadge: { background: "#FBF0E8", color: "#D4620D", borderRadius: 10, padding: "4px 10px", fontWeight: 800, fontSize: 14, letterSpacing: "0.03em", flexShrink: 0, marginTop: 2 },
  modalTitle:   { fontSize: 16, fontWeight: 700, color: "var(--text)", lineHeight: 1.3 },
  modalMeta:    { fontSize: 12, color: "var(--text-2)", marginTop: 4 },
  modalBody:    { overflowY: "auto", padding: "20px", flex: 1 },
  closeBtn:     { background: "var(--bg-subtle)", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text-2)", flexShrink: 0 },

  section:      { marginBottom: 24 },
  sectionTitle: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-2)", marginBottom: 12 },

  photosGrid:   { display: "flex", gap: 12, flexWrap: "wrap" },
  photoCard:    { display: "flex", flexDirection: "column", gap: 6 },
  photoCardLabel: { fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em" },
  photoCardImg:   { width: 180, height: 140, objectFit: "cover", borderRadius: 12, cursor: "pointer", border: "1px solid var(--border)", transition: "opacity 0.15s" },

  dataGrid:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  dataItem:  { background: "var(--bg-subtle)", borderRadius: 10, padding: "10px 14px" },
  dataLabel: { fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "flex", alignItems: "center" },
  dataValue: { fontSize: 13, color: "var(--text)", fontWeight: 500, lineHeight: 1.5 },
  mapLink:   { color: "#D4620D", textDecoration: "none", fontSize: 13, fontWeight: 700 },

  lightboxOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" },
  lightboxClose:   { position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  lightboxImg:     { maxWidth: "90vw", maxHeight: "88vh", borderRadius: 10 },
};
